import {
  Email as EmailIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material'
import { AxiosError } from 'axios'
import { prepareDataForValidation } from 'formik'
import { trim } from 'lodash'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { EActionsKey, THeadCellWithActions } from '~/components/table/models'
import { NEW_LINE } from '~/constants'
import { TAgent } from '~/features/agents/models'
import {
  CAMPAIGN_BUILDER_ASSISTANT_SEARCH_PARAM,
  CAMPAIGN_BUILDER_FIELD_SEARCH_PARAM,
  CAMPAIGN_BUILDER_STEP_SEARCH_PARAM,
  getFormKeyToBuilderStepId,
  getRequiredFieldsCandidates,
} from '~/features/campaigns/components/campaignBuilder/campaignBuilderContent/constants'
import {
  campaignBuilderValidationSchema,
  getCampaignExecutionType,
  getValueByNormalizedVariable,
} from '~/features/campaigns/components/campaignBuilder/campaignBuilderForm/utils'
import { scheduleStepSendTimeFromPayload } from '~/features/campaigns/components/campaignBuilder/utils'
import {
  ECampaignAgentAccessScope,
  ECampaignBuilderFormKeys,
  ECampaignBuilderStepIds,
  ECampaignChannel,
  ECampaignAgentJunctionStatus,
  ECampaignContactListItemKeys,
  ECampaignKeys,
  ECampaignScheduleStepKeys,
  ECampaignTabs,
  ECampaignType,
  EOfuCadence,
  EOfuOutboundEmailProvider,
  EStructuredTimingMode,
  TCampaignError,
  TCampaign,
  TCampaignAgentJunction,
  TCampaignGetContact,
  TCampaignGetContactListItem,
  TCampaignScheduleStep,
  TCampaignScheduleStepForm,
  TMergeField,
  TOutreach,
  TVariableToColumnMap,
  TCustomProperty,
  TMappedContact,
  EOutreachStatus,
  TCampaignEmailMetrics,
  EEmailStatus,
  TCampaignTemplateFilters,
  EOfuCampaignTabs,
  EOfuContactsTab,
  TCampaignContactsFilterState,
} from '~/features/campaigns/models'
import { getCampaignJunctions } from '~/features/campaigns/utils/campaignJunctions'
import {
  CAMPAIGN_DEFAULT_MERGE_FIELDS,
  DEFAULT_OFU_QUALIFICATION_CRITERIA,
  DEFAULT_MAX_REACHOUTS,
  INSERT_INPUT_MAX_LENGTH,
} from '~/features/campaigns/utils/constants'
import { getParsedOfuOutreachTime } from '~/features/campaigns/utils/date'
import { getOutreachInstructionsDisplayText } from '~/features/campaigns/utils/outreachInstructions'
import {
  EEmailStatus as EEmailSettingsStatus,
  EOutboundPhoneStatus,
  EPhoneStatus,
  ETextStatus,
} from '~/features/settings/models'
import { EClientPath, EServerPath } from '~/models/api'
import { ETestId } from '~/models/tests'
import pathBuilder from '~/utils/api'
import { formatLocaleDateTime, formatLocaleMonthDay } from '~/utils/date'
import { escapeRegExp } from '~/utils/regExp'

export const queryKeys = {
  campaignsBase: (agentId: string) => [EServerPath.campaigns, agentId],
  campaigns: (
    agentId: string,
    archived = false,
    visibility?: string,
    orgId?: string,
  ) => [
    EServerPath.campaigns,
    agentId,
    archived,
    ...(visibility !== undefined ? [visibility] : []),
    ...(orgId !== undefined ? [orgId] : []),
  ],
  campaign: (id: string) => [EServerPath.campaign, id],
  campaignAdvanceTimePreview: (id: string) => [
    EServerPath.campaignAdvanceTime,
    id,
  ],
  campaignScheduleSteps: (id: string) => [
    EServerPath.campaignScheduleSteps,
    id,
  ],
  campaignExecute: (id: string) => [EServerPath.campaignExecute, id],
  campaignContact: (id: string, campaignContactId: string) => [
    EServerPath.campaignContact,
    id,
    campaignContactId,
  ],
  campaignContacts: (id: string) => [EServerPath.campaignContacts, id],
  campaignUploadLogs: (id: string) => [EServerPath.campaignUploadLogs, id],
  campaignUploadLog: (id: string, uploadLogId: string) => [
    EServerPath.campaignUploadLog,
    id,
    uploadLogId,
  ],
  campaignContactsPaginated: (
    id: string,
    limit: number,
    search?: string,
    outreachFilter?: string,
    contactStatusFilter?: string[],
    sourceTypeFilter?: string[],
    assistantFilter?: string[],
    sourceId?: string,
    sortBy?: string,
    sortDirection?: string,
    goalFilter?: string[],
    sentimentFilter?: string[],
  ) => [
    EServerPath.campaignContactsPaginated,
    id,
    'paginated',
    limit,
    ...(search ? [search] : []),
    ...(outreachFilter ? [outreachFilter] : []),
    ...(contactStatusFilter?.length ? contactStatusFilter : []),
    ...(sourceTypeFilter?.length ? sourceTypeFilter : []),
    ...(assistantFilter?.length ? assistantFilter : []),
    ...(sourceId ? [sourceId] : []),
    ...(sortBy ? [sortBy] : []),
    ...(sortDirection ? [sortDirection] : []),
    // Prefixed so these values can't collide with another filter's in the key.
    ...(goalFilter?.length ? ['goal', ...goalFilter] : []),
    ...(sentimentFilter?.length ? ['sentiment', ...sentimentFilter] : []),
  ],
  campaignConversations: (id: string) => [
    EServerPath.campaignConversations,
    id,
  ],
  campaignEmailMetrics: (id: string, agents?: string[]) => [
    EServerPath.campaignEmailServiceMetrics,
    id,
    ...(agents?.length ? agents : []),
  ],
  campaignsPhoneReports: (id: string, agents?: string[]) => [
    EServerPath.campaignsPhoneReports,
    id,
    ...(agents?.length ? agents : []),
  ],

  campaignsSummaryReport: (id: string, agents?: string[]) => [
    EServerPath.campaignsSummaryReport,
    id,
    ...(agents?.length ? agents : []),
  ],
  campaignsTextReports: (id: string, agents?: string[]) => [
    EServerPath.campaignsTextReports,
    id,
    ...(agents?.length ? agents : []),
  ],
  importConfigs: (campaignId: string) => [
    EServerPath.crmIntegrations,
    campaignId,
  ],
  importConfig: (id: string) => [EServerPath.crmIntegration, id],
  campaignsTemplates: ({ filters }: { filters?: TCampaignTemplateFilters }) => [
    EClientPath.campaignTemplates,
    filters,
  ],
  campaignsTemplatesVerticalTags: () => [
    EServerPath.campaignsTemplatesVerticalTags,
  ],
}

export function getOutreachFilterParam(
  state: TCampaignContactsFilterState,
): string {
  return state.outreachFilter.length === 0
    ? 'all'
    : state.outreachFilter.join(',')
}

export const getTabFromRoute = (pathname: string, id: string) => {
  switch (pathname) {
    case pathBuilder(EClientPath.campaignContacts, { id }):
    case pathBuilder(EClientPath.campaignOfuContacts, { id }):
    case pathBuilder(EClientPath.campaignOfuContactsLegacy, { id }):
      return ECampaignTabs.contacts
    case pathBuilder(EClientPath.campaignResults, { id }):
    case pathBuilder(EClientPath.campaignOfuPerformance, { id }):
    case pathBuilder(EClientPath.campaignOfuResultsLegacy, { id }):
      return ECampaignTabs.results
    case pathBuilder(EClientPath.campaignDetails, { id }):
    case pathBuilder(EClientPath.campaignOfuDetails, { id }):
    case pathBuilder(EClientPath.campaignOfuDetailsLegacy, { id }):
    default:
      return ECampaignTabs.details
  }
}

export const getOfuTabFromRoute = (pathname: string, id: string) => {
  switch (pathname) {
    case pathBuilder(EClientPath.campaignContacts, { id }):
    case pathBuilder(EClientPath.campaignOfuContacts, { id }):
      return EOfuCampaignTabs.contacts
    case pathBuilder(EClientPath.campaignOfuPerformance, { id }):
      return EOfuCampaignTabs.performance
    case pathBuilder(EClientPath.campaignOfuAssistants, { id }):
      return EOfuCampaignTabs.assistants
    case pathBuilder(EClientPath.campaignOfuConfiguration, { id }):
      return EOfuCampaignTabs.edit
    default:
      return EOfuCampaignTabs.contacts
  }
}

export const getCampaignEditPath = ({
  id,
  stepId,
  assistantId,
  fieldPath,
}: {
  id: string
  stepId?: string
  assistantId?: string
  fieldPath?: string
}) => {
  const basePath = pathBuilder(EClientPath.campaignOfuConfiguration, { id })
  const nextSearchParams = new URLSearchParams()

  if (stepId) {
    nextSearchParams.set(CAMPAIGN_BUILDER_STEP_SEARCH_PARAM, stepId)
  }
  if (assistantId) {
    nextSearchParams.set(CAMPAIGN_BUILDER_ASSISTANT_SEARCH_PARAM, assistantId)
  }
  if (fieldPath) {
    nextSearchParams.set(CAMPAIGN_BUILDER_FIELD_SEARCH_PARAM, fieldPath)
  }

  const query = nextSearchParams.toString()
  return query ? `${basePath}?${query}` : basePath
}

export const OFU_CONTACTS_TAB_SEARCH_KEY = 'tab'
export const OFU_CONTACTS_SOURCE_ID_KEY = 'sourceId'
export const OFU_CONTACTS_SOURCE_FILE_NAME_KEY = 'sourceFileName'
export const OFU_CONTACTS_ASSISTANT_FILTER_KEY = 'assistantId'

export const getJunctionExecutionStatus = (
  junction?: TCampaignAgentJunction | null,
): ECampaignAgentJunctionStatus => {
  const parsedJunction = (junction || null) as
    | (TCampaignAgentJunction & {
        executionStatus?: ECampaignAgentJunctionStatus | null
        ofu_executionStatus?: ECampaignAgentJunctionStatus | null
        ofuExecutionStatus?: ECampaignAgentJunctionStatus | null
      })
    | null

  return (
    (parsedJunction?.status as ECampaignAgentJunctionStatus) ||
    (parsedJunction?.executionStatus as ECampaignAgentJunctionStatus) ||
    (parsedJunction?.ofu_executionStatus as ECampaignAgentJunctionStatus) ||
    (parsedJunction?.ofuExecutionStatus as ECampaignAgentJunctionStatus) ||
    ECampaignAgentJunctionStatus.notStarted
  )
}

export const isJunctionActive = (
  junction?: TCampaignAgentJunction | null,
): boolean => {
  const parsedJunction = (junction || null) as
    | (TCampaignAgentJunction & {
        executionStatus?: string | null
        ofu_executionStatus?: string | null
        ofuExecutionStatus?: string | null
      })
    | null
  const rawStatus = String(
    parsedJunction?.status ||
      parsedJunction?.executionStatus ||
      parsedJunction?.ofu_executionStatus ||
      parsedJunction?.ofuExecutionStatus ||
      '',
  ).trim()

  return rawStatus === ECampaignAgentJunctionStatus.started
}

export const hasAnyLiveAssistant = (
  campaign: Record<string, unknown> | null | undefined,
): boolean =>
  getCampaignJunctions(campaign).some((junction) => isJunctionActive(junction))

export const getCampaignAssistantDisplayName = (
  junction?: TCampaignAgentJunction | null,
): string =>
  junction?.Agent?.agentReferenceName ||
  junction?.Agent?.agentName ||
  junction?.Agent?.businessName ||
  junction?.agentId ||
  ''

export const getRequiredCampaignAgentMetadataFields = (
  campaign: Record<string, unknown> | null | undefined,
): TMergeField[] => {
  const metadataFields =
    (campaign?.[ECampaignKeys.agentMetadataKeys] as
      | TMergeField[]
      | undefined) || []

  return metadataFields.filter((field) => field.required)
}

export const getMissingRequiredAgentMetadataForJunction = ({
  campaign,
  junction,
}: {
  campaign: Record<string, unknown> | null | undefined
  junction?: TCampaignAgentJunction | null
}): TMergeField[] => {
  const requiredFields = getRequiredCampaignAgentMetadataFields(campaign)
  if (!requiredFields.length) {
    return []
  }

  const metadata = (junction?.agentMetadata || {}) as Record<string, unknown>
  const assistantData = (junction?.Agent || {}) as Record<string, unknown>

  return requiredFields.filter((field) => {
    const lookupKey = String(field.variable || field.friendlyName || '').trim()
    if (!lookupKey) {
      return true
    }

    const metadataValue = getValueByNormalizedVariable(metadata, lookupKey)
    if (metadataValue) {
      return false
    }

    const assistantFallbackValue = getValueByNormalizedVariable(
      assistantData,
      lookupKey,
    )
    return !assistantFallbackValue
  })
}

export const hasRequiredAgentMetadataForJunction = ({
  campaign,
  junction,
}: {
  campaign: Record<string, unknown> | null | undefined
  junction?: TCampaignAgentJunction | null
}): boolean => {
  return (
    getMissingRequiredAgentMetadataForJunction({ campaign, junction })
      .length === 0
  )
}

export const getOfuContactsTabFromSearchParams = (
  searchParams: URLSearchParams,
): EOfuContactsTab => {
  const tab = searchParams.get(OFU_CONTACTS_TAB_SEARCH_KEY)
  if (tab === EOfuContactsTab.importHistory || tab === EOfuContactsTab.add) {
    return tab
  }
  return EOfuContactsTab.all
}

export const parseCsvRow = (row: string): string[] => {
  const result: string[] = []
  let inQuotes = false
  let value = ''

  for (let i = 0; i < row.length; i += 1) {
    const char = row[i]

    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      // If we hit a comma and we're not inside quotes, push the value to the result
      result.push(value.trim())
      value = ''
    } else {
      value += char
    }
  }

  result.push(value.trim())

  return result
}

export const campaignValidationSchema = yup.object().shape({
  [ECampaignKeys.introMessage]: yup
    .string()
    .max(INSERT_INPUT_MAX_LENGTH)
    .required('Intro message is required'),
  [ECampaignKeys.voicemailMessage]: yup
    .string()
    .max(INSERT_INPUT_MAX_LENGTH)
    .required('Voicemail message is required'),
  [ECampaignKeys.campaignName]: yup
    .string()
    .required('Campaign name is required'),
  [ECampaignKeys.questions]: yup
    .string()
    .trim()
    .required('Conversation Questions are required'),
})

type TCheckConnectedFieldsProps = {
  checkChangedFieldValue: string
  requiredFieldValue: string
}

const checkConnectedFields = ({
  checkChangedFieldValue,
  requiredFieldValue,
}: TCheckConnectedFieldsProps): boolean => {
  const hasFieldChanged =
    checkChangedFieldValue && checkChangedFieldValue.trim().length > 0
  const hasRequiredField =
    requiredFieldValue && requiredFieldValue.trim().length > 0

  // If checked field is provided, required field must be provided
  if (hasFieldChanged && !hasRequiredField) {
    return false
  }

  return true
}

export const ofuCampaignValidationSchema = yup.object().shape({
  [ECampaignKeys.id]: yup.string(),
  [ECampaignKeys.agentId]: yup.string(),
  [ECampaignKeys.campaignName]: yup
    .string()
    .required('Campaign name is required'),
  [ECampaignKeys.campaignType]: yup.string().required(),
  [ECampaignKeys.channel]: yup.string().required(),
  [ECampaignKeys.status]: yup.string(),
  [ECampaignKeys.createdDate]: yup.date(),
  [ECampaignKeys.updatedDate]: yup.date(),
  [ECampaignKeys.ofuGoal]: yup.string().required('Goal is required'),
  [ECampaignKeys.ofuQualificationCriteria]: yup
    .string()
    .required('Qualification criteria is required'),
  [ECampaignKeys.startDate]: yup.date().required('Start date is required'),
  [ECampaignKeys.endDate]: yup.date().nullable(),
  [ECampaignKeys.ofuOutreachTime]: yup
    .string()
    .required('Outreach time is required'),
  [ECampaignKeys.ofuIsManual]: yup.boolean(),
  [ECampaignKeys.ofuMaxReachouts]: yup
    .number()
    .min(1, 'Max reach outs is required')
    .max(20, 'Max reach outs must be less than or equal to 20')
    .required('Max reach outs is required'),
  [ECampaignKeys.ofuCadence]: yup.string().required('Cadence is required'),
  [ECampaignKeys.ofuChannels]: yup
    .array()
    .min(1, 'Select at least one channel')
    .required('Channels are required'),
  [ECampaignKeys.ofuCadenceCustom]: yup
    .string()
    .when(ECampaignKeys.ofuCadence, {
      is: (val: string) => val === 'custom',
      then: (schema) => schema.required('Custom cadence schedule is required'),
      otherwise: (schema) => schema.nullable(),
    }),
  [ECampaignKeys.ofuCustomEmailService]: yup
    .string()
    .url('Must be a valid URL')
    .nullable(),
  [ECampaignKeys.ofuCustomEmailBody]: yup
    .mixed()
    .test('is-valid-json', 'Must be a valid JSON object', (value) => {
      if (!value) return true // Allow null/undefined
      if (typeof value === 'object') return true // Allow objects
      try {
        const parsed = JSON.parse(value as string)
        return typeof parsed === 'object' && parsed !== null
      } catch {
        return false
      }
    })
    .nullable(),
  [ECampaignKeys.ofuOutboundEmailProvider]: yup
    .string()
    .required('Email provider is required'),
  [ECampaignKeys.ofuOutboundEmailProviderId]: yup
    .string()
    .when(ECampaignKeys.ofuOutboundEmailProvider, {
      is: (emailProvider: string) =>
        emailProvider !== EOfuOutboundEmailProvider.internal,
      then: (schema) => schema.required('Please select an email provider'),
      otherwise: (schema) => schema.nullable(),
    }),

  [ECampaignKeys.introMessage]: yup
    .string()
    .nullable()
    .test(
      'intro-voicemail-required',
      'Intro message is required when voicemail message is provided',
      function introVoicemailRequired(value) {
        return this.parent[ECampaignKeys.ofu_firstOutreachChannel] ===
          ECampaignChannel.phone
          ? checkConnectedFields({
              checkChangedFieldValue:
                this.parent[ECampaignKeys.voicemailMessage] || '',
              requiredFieldValue: value || '',
            })
          : true
      },
    ),

  [ECampaignKeys.voicemailMessage]: yup
    .string()
    .nullable()
    .test(
      'voicemail-intro-required',
      'Voicemail message is required when intro message is provided',
      function voicemailIntroRequired(value) {
        return this.parent[ECampaignKeys.ofu_firstOutreachChannel] ===
          ECampaignChannel.phone
          ? checkConnectedFields({
              checkChangedFieldValue:
                this.parent[ECampaignKeys.introMessage] || '',
              requiredFieldValue: value || '',
            })
          : true
      },
    ),

  [ECampaignKeys.firstEmailSubject]: yup
    .string()
    .nullable()
    .test(
      'email-subject-body-required',
      'Email subject is required when email body is provided',
      function emailSubjectBodyRequired(value) {
        return this.parent[ECampaignKeys.ofu_firstOutreachChannel] ===
          ECampaignChannel.email
          ? checkConnectedFields({
              checkChangedFieldValue:
                this.parent[ECampaignKeys.firstEmailBody] || '',
              requiredFieldValue: value || '',
            })
          : true
      },
    ),

  [ECampaignKeys.firstEmailBody]: yup
    .string()
    .nullable()
    .test(
      'email-body-subject-required',
      'Email body is required when email subject is provided',
      function emailBodySubjectRequired(value) {
        return this.parent[ECampaignKeys.ofu_firstOutreachChannel] ===
          ECampaignChannel.email
          ? checkConnectedFields({
              checkChangedFieldValue:
                this.parent[ECampaignKeys.firstEmailSubject] || '',
              requiredFieldValue: value || '',
            })
          : true
      },
    ),
})

/** Maps raw schema error messages to user-friendly copy for the Go live tooltip. */
const GO_LIVE_ERROR_MESSAGE_MAP: Record<string, string> = {
  'ofu_isManual cannot be null':
    'Execution type (manual or automatic) is required',
}

/**
 * User-friendly labels for campaign builder form fields (schema field names appear in yup errors).
 * Uses ECampaignBuilderFormKeys so the schema field name is the enum value, not duplicated.
 */
const GO_LIVE_ERROR_FIELD_LABELS: Partial<
  Record<ECampaignBuilderFormKeys, string>
> = {
  [ECampaignBuilderFormKeys.title]: 'Campaign title',
  [ECampaignBuilderFormKeys.knowledge]: 'Knowledge base',
  [ECampaignBuilderFormKeys.additionalInstructions]: 'Additional instructions',
  [ECampaignBuilderFormKeys.ofuGoal]: 'Goal',
  [ECampaignBuilderFormKeys.contactEligibilityCriteria]:
    'Qualification criteria',
  [ECampaignBuilderFormKeys.ofu_outreachTime]: 'Outreach time',
  [ECampaignBuilderFormKeys.ofuIsManual]: 'Execution type',
  [ECampaignBuilderFormKeys.maxReachouts]: 'Max reach outs',
  [ECampaignBuilderFormKeys.cadence]: 'Cadence',
  [ECampaignBuilderFormKeys.cadenceCustom]: 'Custom cadence schedule',
  [ECampaignBuilderFormKeys.ofu_channels]: 'Communication channels',
  [ECampaignBuilderFormKeys.channelOfFirstOutreach]:
    'Channel of first outreach',
  [ECampaignBuilderFormKeys.emailProvider]: 'Email provider',
  [ECampaignBuilderFormKeys.emailProviderId]: 'Email provider',
  [ECampaignBuilderFormKeys.emailProviderCustomEmailBody]: 'Custom email body',
  [ECampaignBuilderFormKeys.emailContent]: 'Email content',
  [ECampaignBuilderFormKeys.emailSubject]: 'Email subject',
  [ECampaignBuilderFormKeys.textMessage]: 'Text message',
  [ECampaignBuilderFormKeys.phoneIntroMessage]: 'Phone intro message',
  [ECampaignBuilderFormKeys.phoneVoicemailMessage]: 'Phone voicemail message',
  [ECampaignBuilderFormKeys.startDate]: 'Start date',
  [ECampaignBuilderFormKeys.endDate]: 'End date',
  [ECampaignBuilderFormKeys.mergeFields]: 'Merge fields',
  [ECampaignBuilderFormKeys.messageStrategy]: 'Message strategy',
  [ECampaignBuilderFormKeys.executionMode]: 'Execution mode',
  [ECampaignBuilderFormKeys.description]: 'Description',
}

export const getArrayFromStringList = (
  value: string | null | undefined,
): string[] | undefined => {
  const resultArray = (value || '')
    .replaceAll(/• /g, '')
    .split(NEW_LINE)
    .map(trim)
    .filter(Boolean)

  return resultArray.length > 0 ? resultArray : undefined
}

export {
  getOutreachInstructionsDisplayText,
  getOutreachInstructionsSaveFields,
  legacyRulesToText,
  normalizeOutreachInstructionsText,
} from '~/features/campaigns/utils/outreachInstructions'

export const getStringListFromArray = (
  value: string[] | null | undefined,
): string =>
  (value || [])
    .map((line) => {
      if (line.startsWith('• ') || line === '•') {
        return line
      }

      if (line.startsWith('•')) {
        return `• ${line.slice(1)}`
      }

      return `• ${line}`
    })
    .filter((line) => line !== '•')
    .join(NEW_LINE)

export const getRequiredMergeFieldByChannel = (channel: ECampaignChannel) => {
  switch (channel) {
    case ECampaignChannel.email:
      return CAMPAIGN_DEFAULT_MERGE_FIELDS.EMAIL
    case ECampaignChannel.text:
    case ECampaignChannel.phone:
    default:
      return CAMPAIGN_DEFAULT_MERGE_FIELDS.PHONE_NUMBER
  }
}

const CAMPAIGN_CHANNEL_VALUES = Object.values(ECampaignChannel)

const isValidCampaignChannel = (value: unknown): value is ECampaignChannel =>
  CAMPAIGN_CHANNEL_VALUES.includes(value as ECampaignChannel)

export const getCampaignChannelsWithFallback = (
  campaign: Record<string, unknown> | null | undefined,
): ECampaignChannel[] => {
  const rawOfuChannels = campaign?.[ECampaignKeys.ofuChannels]
  if (Array.isArray(rawOfuChannels)) {
    const normalizedOfuChannels = rawOfuChannels.filter(isValidCampaignChannel)
    return Array.from(new Set(normalizedOfuChannels))
  }

  const firstOutreachChannel = campaign?.[
    ECampaignKeys.ofu_firstOutreachChannel
  ] as unknown
  const legacyChannel = campaign?.[ECampaignKeys.channel] as unknown

  return Array.from(
    new Set(
      [firstOutreachChannel, legacyChannel].filter(isValidCampaignChannel),
    ),
  )
}

export const getRequiredMergeFields = (
  channel: ECampaignChannel | ECampaignChannel[],
) => {
  const result: string[] = [CAMPAIGN_DEFAULT_MERGE_FIELDS.FIRST_NAME]

  if (Array.isArray(channel)) {
    channel.forEach((c) => {
      result.push(getRequiredMergeFieldByChannel(c))
    })
  } else {
    result.push(getRequiredMergeFieldByChannel(channel))
  }

  const uniqueRequiredFields = Array.from(new Set(result))

  return uniqueRequiredFields.map(
    (field) => ({ friendlyName: field, required: true }) as TMergeField,
  )
}

export const getMergeFields = (
  existingMergeFields: TMergeField[] | null | undefined,
  channel: ECampaignChannel | ECampaignChannel[],
) => {
  const result: TMergeField[] = (existingMergeFields || []).map((field) => {
    switch (field.friendlyName) {
      case CAMPAIGN_DEFAULT_MERGE_FIELDS.FIRST_NAME:
        return {
          ...field,
          required: true,
          variable: field.variable || (undefined as never),
        }
      case CAMPAIGN_DEFAULT_MERGE_FIELDS.EMAIL:
        return {
          ...field,
          required: Array.isArray(channel)
            ? channel.includes(ECampaignChannel.email)
            : channel === ECampaignChannel.email,
          variable: field.variable || (undefined as never),
        }
      case CAMPAIGN_DEFAULT_MERGE_FIELDS.PHONE_NUMBER:
        return {
          ...field,
          required: Array.isArray(channel)
            ? channel.includes(ECampaignChannel.phone) ||
              channel.includes(ECampaignChannel.text)
            : channel !== ECampaignChannel.email,
          variable: field.variable || (undefined as never),
        }
      default:
        return {
          ...field,
          required: false,
          variable: field.variable || (undefined as never),
        }
    }
  })

  const requiredMergeFields = getRequiredMergeFields(channel)

  const missingRequiredFields = requiredMergeFields.filter(
    (requiredField) =>
      !result.some(
        (resultField) =>
          resultField.friendlyName === requiredField.friendlyName,
      ),
  )

  if (missingRequiredFields.length) {
    result.unshift(...missingRequiredFields)
  }

  const dedupedByKey = new Map<string, TMergeField>()
  result.forEach((field) => {
    const dedupeKey = String(field.variable || field.friendlyName || '')
      .trim()
      .toLowerCase()
    if (!dedupeKey) {
      return
    }

    const existingField = dedupedByKey.get(dedupeKey)
    if (!existingField) {
      dedupedByKey.set(dedupeKey, field)
      return
    }

    dedupedByKey.set(dedupeKey, {
      ...existingField,
      ...field,
      required: Boolean(existingField.required || field.required),
      variable: existingField.variable || field.variable || null,
      friendlyName: existingField.friendlyName || field.friendlyName,
    })
  })

  return Array.from(dedupedByKey.values())
}

function formatGoLiveErrorForDisplay(
  message: string,
  campaignType?: ECampaignType,
): string {
  const exact = GO_LIVE_ERROR_MESSAGE_MAP[message]
  if (exact) return exact

  const labels =
    campaignType === ECampaignType.ofuOneTime
      ? {
          ...GO_LIVE_ERROR_FIELD_LABELS,
          [ECampaignBuilderFormKeys.startDate]: 'Scheduled date',
        }
      : GO_LIVE_ERROR_FIELD_LABELS

  let out = message
  const schemaFieldToLabel = Object.entries(labels)
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .sort(([a], [b]) => b.length - a.length)

  out = schemaFieldToLabel.reduce(
    (acc, [schemaFieldName, label]) =>
      acc.replace(new RegExp(escapeRegExp(schemaFieldName), 'g'), label),
    out,
  )
  out = out.replace(/cannot be null/g, 'is required')
  return out
}

function mapScheduleStepsToFormShape(
  steps: TCampaignScheduleStep[],
): TCampaignScheduleStepForm[] {
  return steps.map((step) => ({
    ...step,
    [ECampaignScheduleStepKeys.sendTime]: scheduleStepSendTimeFromPayload(
      step[ECampaignScheduleStepKeys.sendTime],
    ),
  }))
}

export type TGoLiveValidationOptions = {
  timezone?: string
  selectedAgent?: TAgent
  /**
   * Prefer `GET /campaigns/:id/schedule-steps`. Campaign GET does not include
   * `CampaignScheduleStep`; omitting this falls back to nested campaign data.
   */
  scheduleSteps?: TCampaignScheduleStep[] | null
}

function campaignToBuilderFormShape(
  campaign: Record<string, unknown>,
  options?: Pick<TGoLiveValidationOptions, 'timezone' | 'scheduleSteps'>,
): Record<string, unknown> {
  const timezone = options?.timezone
  const channels = getCampaignChannelsWithFallback(campaign)
  const mergeFields = getMergeFields(
    campaign[ECampaignKeys.mergeFields] as TMergeField[] | null | undefined,
    channels,
  )
  const ofuOutreachTime = campaign[ECampaignKeys.ofuOutreachTime] as
    | string
    | undefined
  const parsedOfuOutreachTime = getParsedOfuOutreachTime({
    agentTimezone: timezone,
    ofuOutreachTime,
  })
  const campaignFacts =
    (campaign[ECampaignKeys.campaignFacts] as
      | Array<{ id?: string; text?: string; fact?: string }>
      | null
      | undefined) || []
  const campaignJunctions = getCampaignJunctions(campaign)
  const junctionAgentIds = campaignJunctions
    .map((junction) => String(junction.agentId || junction.Agent?.id || ''))
    .filter(Boolean)
  const sharedCampaignAgentIds =
    (campaign[ECampaignKeys.sharedCampaignAgentIds] as string[] | undefined) ||
    junctionAgentIds

  const rawScheduleSteps =
    options?.scheduleSteps !== undefined
      ? (options.scheduleSteps ?? [])
      : ((campaign[ECampaignKeys.campaignScheduleSteps] as
          | TCampaignScheduleStep[]
          | undefined) ?? [])

  return {
    [ECampaignBuilderFormKeys.agentAccessScope]:
      (campaign[ECampaignKeys.agentAccessScope] as ECampaignAgentAccessScope) ||
      ECampaignAgentAccessScope.singleAgent,
    [ECampaignBuilderFormKeys.sharedCampaignAgentIds]: Array.from(
      new Set(sharedCampaignAgentIds.map(String)),
    ),
    [ECampaignBuilderFormKeys.sharedCampaignAgentSettings]:
      (campaign[ECampaignKeys.sharedCampaignAgentSettings] as
        | Record<string, unknown>
        | undefined) || {},
    [ECampaignBuilderFormKeys.title]:
      (campaign[ECampaignKeys.campaignName] as string) || '',
    [ECampaignBuilderFormKeys.ofuGoal]:
      (campaign[ECampaignKeys.ofuGoal] as string) || '',
    [ECampaignBuilderFormKeys.description]:
      (campaign[ECampaignKeys.description] as string) || '',
    [ECampaignBuilderFormKeys.executionMode]: getCampaignExecutionType(
      campaign as Parameters<typeof getCampaignExecutionType>[0],
    ),
    [ECampaignBuilderFormKeys.startDate]:
      (campaign[ECampaignKeys.startDate] as string) || '',
    [ECampaignBuilderFormKeys.endDate]:
      (campaign[ECampaignKeys.endDate] as string) || '',
    [ECampaignBuilderFormKeys.ofu_outreachTime]: parsedOfuOutreachTime,
    [ECampaignBuilderFormKeys.additionalInstructions]:
      getOutreachInstructionsDisplayText({
        outreachInstructionsText: campaign[
          ECampaignKeys.outreachInstructionsText
        ] as string | null | undefined,
        rules: campaign[ECampaignKeys.rules] as string[] | null | undefined,
      }),
    [ECampaignBuilderFormKeys.contactEligibilityCriteria]:
      (campaign[ECampaignKeys.ofuQualificationCriteria] as string) ||
      DEFAULT_OFU_QUALIFICATION_CRITERIA,
    [ECampaignBuilderFormKeys.knowledge]: campaignFacts.map((item) => ({
      ...item,
      id: item?.id,
      text: (item?.text ?? item?.fact ?? '') as string,
    })),
    [ECampaignBuilderFormKeys.messageStrategy]:
      campaign[ECampaignKeys.messageStrategy],
    [ECampaignBuilderFormKeys.channelOfFirstOutreach]:
      campaign[ECampaignKeys.ofu_firstOutreachChannel] || null,
    [ECampaignBuilderFormKeys.ofu_channels]: channels,
    [ECampaignBuilderFormKeys.cadence]:
      (campaign[ECampaignKeys.ofuCadence] as EOfuCadence) ||
      EOfuCadence.aggressive,
    [ECampaignBuilderFormKeys.cadenceCustom]:
      (campaign[ECampaignKeys.ofuCadenceCustom] as string) || '',
    [ECampaignBuilderFormKeys.maxReachouts]:
      (campaign[ECampaignKeys.ofuMaxReachouts] as number) ??
      DEFAULT_MAX_REACHOUTS,
    [ECampaignBuilderFormKeys.phoneIntroMessage]:
      (campaign[ECampaignKeys.introMessage] as string) || '',
    [ECampaignBuilderFormKeys.phoneVoicemailMessage]:
      (campaign[ECampaignKeys.voicemailMessage] as string) || '',
    [ECampaignBuilderFormKeys.emailSubject]:
      (campaign[ECampaignKeys.firstEmailSubject] as string) || '',
    [ECampaignBuilderFormKeys.emailContent]:
      (campaign[ECampaignKeys.firstEmailBody] as string) || '',
    [ECampaignBuilderFormKeys.textMessage]:
      (campaign[ECampaignKeys.firstTextMessage] as string) || '',
    [ECampaignBuilderFormKeys.emailProvider]:
      (campaign[ECampaignKeys.ofuOutboundEmailProvider] as string) ||
      EOfuOutboundEmailProvider.internal,
    [ECampaignBuilderFormKeys.emailProviderCustomEmailBody]:
      campaign[ECampaignKeys.ofuCustomEmailBody],
    [ECampaignBuilderFormKeys.emailProviderId]:
      (campaign[ECampaignKeys.ofuOutboundEmailProviderId] as string) || '',
    [ECampaignBuilderFormKeys.ofuIsManual]:
      campaign[ECampaignKeys.ofuIsManual] ?? null,
    [ECampaignBuilderFormKeys.shouldRedirectToManageCampaign]: false,
    [ECampaignBuilderFormKeys.useStructuredSchedule]:
      campaign[ECampaignKeys.useStructuredSchedule] ?? false,
    [ECampaignBuilderFormKeys.structuredTimingMode]:
      campaign[ECampaignKeys.structuredTimingMode] ||
      EStructuredTimingMode.relative,
    [ECampaignBuilderFormKeys.campaignScheduleSteps]:
      mapScheduleStepsToFormShape(rawScheduleSteps),
    [ECampaignBuilderFormKeys.mergeFields]: mergeFields,
  }
}

export function getGoLiveValidationErrors(
  campaign: Record<string, unknown> | null | undefined,
  options?: TGoLiveValidationOptions,
): string[] {
  if (!campaign) {
    return ['Campaign is not loaded.']
  }

  const campaignType =
    (campaign[ECampaignKeys.campaignType] as ECampaignType) ??
    ECampaignType.ofuOneTime
  const schema =
    campaignBuilderValidationSchema[campaignType] ??
    campaignBuilderValidationSchema[ECampaignType.ofuOneTime]
  const formShape = campaignToBuilderFormShape(campaign, options)
  const preparedFormShape = prepareDataForValidation(formShape)

  try {
    schema.validateSync(preparedFormShape, {
      abortEarly: false,
      context: {
        ...preparedFormShape,
        selectedAgent: options?.selectedAgent,
        campaign: campaign as TCampaign | null,
      },
    })
    return []
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      const fromInner = err.inner?.length
        ? err.inner
            .map((e) => e.message)
            .filter((msg): msg is string => Boolean(msg))
        : []
      if (fromInner.length) {
        return fromInner.map((msg) =>
          formatGoLiveErrorForDisplay(msg, campaignType),
        )
      }
      if (err.errors?.length) {
        return (err.errors as string[]).map((msg) =>
          formatGoLiveErrorForDisplay(msg, campaignType),
        )
      }
    }
    return ['Campaign configuration is incomplete.']
  }
}

export type TGoLiveValidationErrorWithStep = {
  message: string
  stepId?: ECampaignBuilderStepIds
  fieldPath?: ECampaignBuilderFormKeys | string
  friendlyField?: string
}

export function isCampaignScheduleStepsErrorPath(path: string) {
  const root = ECampaignBuilderFormKeys.campaignScheduleSteps

  return (
    path === root || path.startsWith(`${root}.`) || path.startsWith(`${root}[`)
  )
}

export function getGoLiveValidationErrorsWithSteps(
  campaign: Record<string, unknown> | null | undefined,
  options?: TGoLiveValidationOptions,
): TGoLiveValidationErrorWithStep[] {
  if (!campaign) {
    return [{ message: 'Campaign is not loaded.' }]
  }

  const campaignType =
    (campaign[ECampaignKeys.campaignType] as ECampaignType) ??
    ECampaignType.ofuOneTime

  const schema =
    campaignBuilderValidationSchema[campaignType] ??
    campaignBuilderValidationSchema[ECampaignType.ofuOneTime]

  const formShape = campaignToBuilderFormShape(campaign, options)
  const preparedFormShape = prepareDataForValidation(formShape)

  const formKeyToStepId =
    campaignType === ECampaignType.ofu ||
    campaignType === ECampaignType.ofuOneTime
      ? getFormKeyToBuilderStepId(campaignType)
      : getFormKeyToBuilderStepId(ECampaignType.ofuOneTime)

  const pathToFriendly =
    campaignType === ECampaignType.ofu ||
    campaignType === ECampaignType.ofuOneTime
      ? Object.fromEntries(
          getRequiredFieldsCandidates(campaignType).map((c) => [
            c.pathField,
            {
              friendlyField: c.friendlyField,
              stepId: c.stepId,
            },
          ]),
        )
      : {}

  try {
    schema.validateSync(preparedFormShape, {
      abortEarly: false,
      context: {
        ...preparedFormShape,
        selectedAgent: options?.selectedAgent,
        campaign: campaign as TCampaign | null,
      },
    })
    return []
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      const fromInner = err.inner?.length
        ? (() => {
            const byPath = new Map<string, TGoLiveValidationErrorWithStep>()
            const innerErrors = err.inner ?? []

            innerErrors.forEach((e) => {
              const msg = e.message
              const path = e.path as string | undefined

              if (path != null && byPath.has(path)) {
                return
              }

              const isScheduleStepsPath =
                path != null && isCampaignScheduleStepsErrorPath(path)
              const navigationPath = isScheduleStepsPath
                ? ECampaignBuilderFormKeys.campaignScheduleSteps
                : path

              const formKey = navigationPath as
                | ECampaignBuilderFormKeys
                | undefined
              const stepId = formKey
                ? (formKeyToStepId[formKey] as
                    | ECampaignBuilderStepIds
                    | undefined)
                : undefined
              const friendly =
                navigationPath != null
                  ? (
                      pathToFriendly as Record<
                        string,
                        {
                          friendlyField: string
                          stepId: ECampaignBuilderStepIds
                        }
                      >
                    )[navigationPath]
                  : undefined
              const entry: TGoLiveValidationErrorWithStep = {
                message: formatGoLiveErrorForDisplay(msg, campaignType),
                stepId: stepId ?? friendly?.stepId,
                fieldPath: navigationPath,
                friendlyField: friendly?.friendlyField,
              }
              if (path != null) {
                byPath.set(path, entry)
              } else {
                byPath.set(`__no_path_${byPath.size}`, entry)
              }
            })

            return Array.from(byPath.values()).filter((e) => Boolean(e.message))
          })()
        : []
      if (fromInner.length) return fromInner
      if (err.errors?.length) {
        return (err.errors as string[]).map((msg) => ({
          message: formatGoLiveErrorForDisplay(msg, campaignType),
        }))
      }
    }
    return [{ message: 'Campaign configuration is incomplete.' }]
  }
}

export const extractFriendlyErrorMessage = (
  payload: TCampaignError,
): string => {
  const { message, error } = payload
  const { formErrors = [], fieldErrors = {} } = error || {}

  let errorMessage = message

  // If form errors exist, add them to the errorMessage
  if (formErrors?.length > 0) {
    errorMessage += `\nForm Errors:\n- ${formErrors?.join('\n- ')}`
  }

  // If field errors exist, add them to the errorMessage
  if (Object.keys(fieldErrors || {}).length > 0) {
    errorMessage += `\nField Errors:`
    Object.entries(fieldErrors || {}).forEach(([field, errors]) => {
      const uniqueErrors = Array.from(new Set(errors))
      errorMessage += `\n- ${field}: ${uniqueErrors.join(', ')}`
    })
  }

  return errorMessage
}

export const getContactValueByVariable = (
  variableToColumnMap: TVariableToColumnMap[],
  contact: Record<string, string>,
  variable: string,
  autoParsedColumnTitles?: {
    columnTitle: string
    dbFieldConventionalName: string
  }[],
) => {
  const mapItem = variableToColumnMap.find((item) => item.variable === variable)

  const keyName = mapItem?.column

  if (!keyName && !autoParsedColumnTitles) {
    return undefined as never
  }

  const autoParsedColumnTitle = (autoParsedColumnTitles || []).find(
    (field) => field.dbFieldConventionalName === variable,
  )?.columnTitle

  if (autoParsedColumnTitle) {
    return contact[autoParsedColumnTitle] || (undefined as never)
  }

  return contact[keyName!] || (undefined as never)
}

export const hasSimilarities = (
  variableByUser: string,
  csvColumnTitle: string,
  ourTarget: string,
) => {
  const lowercaseVariableByUser = variableByUser
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')
  const lowercaseCsvColumnTitle = csvColumnTitle
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')
  const lowercaseOurTarget = ourTarget
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')
  const ourPartialTarget = ourTarget
    .split(' ')[0]
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')

  return (
    lowercaseOurTarget.includes(lowercaseVariableByUser) ||
    lowercaseOurTarget.includes(lowercaseCsvColumnTitle) ||
    lowercaseVariableByUser.includes(lowercaseOurTarget) ||
    lowercaseCsvColumnTitle.includes(lowercaseOurTarget) ||
    ourPartialTarget.includes(lowercaseVariableByUser) ||
    ourPartialTarget.includes(lowercaseCsvColumnTitle) ||
    lowercaseVariableByUser.includes(ourPartialTarget) ||
    lowercaseCsvColumnTitle.includes(ourPartialTarget)
  )
}

export const showToastOnCampaignErrorResponse = (
  result: Error,
  defaultMessage: string,
) => {
  try {
    const errorData = (result as AxiosError).response?.data as TCampaignError

    if (errorData.error) {
      toast.error(`${defaultMessage} ${extractFriendlyErrorMessage(errorData)}`)
    }
  } catch {
    toast.error(defaultMessage)
  }
}

export const getCampaignContactsHeadCells = (
  isNoSmallScreen: boolean,
): THeadCellWithActions<TCampaignGetContactListItem> =>
  [
    {
      id: ECampaignContactListItemKeys.firstName,
      label: 'First Name',
      isSortable: true,
    },
    {
      id: ECampaignContactListItemKeys.phone,
      label: 'Phone',
      colSpan: isNoSmallScreen ? 1 : 2,
      isSortable: true,
    },
    {
      id: ECampaignContactListItemKeys.customVariables,
      label: 'Custom Variables',
    },
    {
      id: ECampaignContactListItemKeys.preview,
      label: 'Preview',
    },
    {
      id: ECampaignContactListItemKeys.status,
      label: 'Status',
      isSortable: true,
    },
    isNoSmallScreen
      ? {
          id: ECampaignContactListItemKeys.testContact,
          label: 'Test Contact',
        }
      : null,
    isNoSmallScreen
      ? {
          id: ECampaignContactListItemKeys.createdDate,
          label: 'Added date',
          isSortable: true,
        }
      : null,
    {
      id: EActionsKey.actions,
      label: 'Actions',
    },
  ].filter(Boolean) as THeadCellWithActions<TCampaignGetContactListItem>

export function stripExtraQuotes(input?: string | null) {
  if (!input) return undefined
  const stripped = input.replace(/^['"](.*)['"]$/, '$1')
  // eslint-disable-next-line consistent-return
  return stripped === '' ? undefined : stripped
}

export function getSortedOutreachesByDate(outreaches: TOutreach[]) {
  return outreaches.sort((a, b) => {
    if (a.executedAt && b.executedAt) {
      return new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime()
    }

    if (a.executedAt && !b.executedAt) {
      return -1
    }

    if (!a.executedAt && b.executedAt) {
      return 1
    }

    return 0
  })
}

function getDateString(date: Date) {
  return date.toISOString().split('T')[0]
}

type ChartDataPoint = {
  date: string
  [key: string]: string | number
}

type ProcessChartDataOptions = {
  type: 'contacts' | 'outreaches'
  contacts: TCampaignGetContact[]
  campaignStartDate?: string
  campaignEndDate?: string
}

export type TAutoMappedContactColumns = {
  firstNameColumn: string
  lastNameColumn: string
  emailColumn: string
  phoneColumn: string
  textOptedOutColumn: string
  emailUnsubscribedColumn: string
}

export const processChartData = ({
  type,
  contacts,
  campaignStartDate,
  campaignEndDate,
}: ProcessChartDataOptions): ChartDataPoint[] => {
  if (!campaignStartDate || !contacts.length) return []

  const today = new Date()
  const startDate = new Date(campaignStartDate)

  // Use campaign end date or today, whichever is smaller
  const endDate = campaignEndDate
    ? new Date(Math.min(new Date(campaignEndDate).getTime(), today.getTime()))
    : today

  if (type === 'contacts') {
    // Filter contacts that have creation dates within the campaign period
    const validContacts = contacts.filter((contact) => {
      const creationDate = contact[ECampaignContactListItemKeys.createdDate]
      if (!creationDate) return false
      const contactDate = new Date(creationDate)
      return contactDate >= startDate && contactDate <= endDate
    })

    // Sort contacts by creation date
    validContacts.sort((a, b) => {
      const dateA = new Date(a[ECampaignContactListItemKeys.createdDate] || '')
      const dateB = new Date(b[ECampaignContactListItemKeys.createdDate] || '')
      return dateA.getTime() - dateB.getTime()
    })

    // Create daily data points
    const dataPoints: ChartDataPoint[] = []
    const currentDate = new Date(startDate)
    let contactIndex = 0
    let accumulatedContacts = 0

    while (currentDate <= endDate) {
      const currentDateString = getDateString(currentDate)

      // Count contacts created on this date
      while (
        contactIndex < validContacts.length &&
        validContacts[contactIndex][
          ECampaignContactListItemKeys.createdDate
        ]?.startsWith(currentDateString)
      ) {
        accumulatedContacts += 1
        contactIndex += 1
      }

      dataPoints.push({
        date: formatLocaleMonthDay(currentDate),
        contacts: accumulatedContacts,
      })

      currentDate.setDate(currentDate.getDate() + 1)
    }

    // If there is only one data point, add the last day of the campaign
    if (dataPoints.length === 1) {
      dataPoints.push({
        date: formatLocaleMonthDay(currentDate),
        contacts: accumulatedContacts,
      })
    }

    return dataPoints
  }

  if (type === 'outreaches') {
    // Create dictionary to group outreaches by date
    const outreachesByDate: Record<string, TOutreach[]> = {}

    contacts.forEach((contact) => {
      if (contact.Outreach && contact.Outreach.length > 0) {
        contact.Outreach.forEach((outreach) => {
          // Filter outreaches that have executedAt within the campaign period
          if (outreach.executedAt) {
            const executedDate = new Date(outreach.executedAt)
            if (executedDate >= startDate && executedDate <= endDate) {
              const dateString = getDateString(executedDate)
              if (!outreachesByDate[dateString]) {
                outreachesByDate[dateString] = []
              }
              outreachesByDate[dateString].push(outreach)
            }
          }
        })
      }
    })

    // Get sorted dates and create data points
    const sortedDates = Object.keys(outreachesByDate).sort()
    const dataPoints: ChartDataPoint[] = []
    const channelCounts: { [key: string]: number } = {}

    sortedDates.forEach((dateString) => {
      const outreachesForDate = outreachesByDate[dateString]

      outreachesForDate.forEach((outreach: TOutreach) => {
        channelCounts[outreach.channel] =
          (channelCounts[outreach.channel] || 0) + 1
      })

      const date = new Date(dateString)
      dataPoints.push({
        date: formatLocaleMonthDay(date),
        ...channelCounts,
      })
    })

    // If there is only one data point, add the last day of the campaign
    if (dataPoints.length === 1) {
      dataPoints.push({
        date: formatLocaleMonthDay(endDate),
        ...channelCounts,
      })
    }

    return dataPoints
  }

  return []
}

export const getChannelOptions = (selectedAgent: TAgent | null | undefined) => [
  {
    id: 'phone',
    value: 'phone',
    label: 'Phone',
    icon: PhoneIcon,
    enabled:
      selectedAgent?.phoneStatus === EPhoneStatus.active &&
      selectedAgent?.outboundPhoneStatus === EOutboundPhoneStatus.active &&
      Boolean(selectedAgent?.phoneNumber),
    disabledMessage: 'Phone calls must be enabled and a phone number selected.',
    settingsLink: EClientPath.settingsPhone,
    settingsLinkText: 'Go to Phone Settings',
    testId: ETestId.CampaignBuilderCommunicationChannelPhone,
  },
  {
    id: 'email',
    value: 'email',
    label: 'Email',
    icon: EmailIcon,
    enabled:
      selectedAgent?.emailStatus === EEmailSettingsStatus.active &&
      Boolean(selectedAgent?.agentEmail),
    disabledMessage: 'Email must be enabled and an email address selected.',
    settingsLink: EClientPath.settingsEmail,
    settingsLinkText: 'Go to Email Settings',
    testId: ETestId.CampaignBuilderCommunicationChannelEmail,
  },
  {
    id: 'text',
    value: 'text',
    label: 'Text',
    icon: MessageIcon,
    enabled:
      selectedAgent?.textStatus === ETextStatus.active &&
      Boolean(selectedAgent?.phoneNumber),
    disabledMessage: 'Texting must be enabled and a phone number selected.',
    settingsLink: EClientPath.settingsText,
    settingsLinkText: 'Go to Text Settings',
    testId: ETestId.CampaignBuilderCommunicationChannelText,
  },
]

type TCampaignChannelSource = {
  channel?: ECampaignChannel | null
  ofu_channels?: ECampaignChannel[] | null
}

export const getCampaignChannels = (
  campaign: TCampaignChannelSource | null | undefined,
): ECampaignChannel[] => {
  if (!campaign) return []

  const oldChannel = campaign.channel
  const ofuChannels = campaign.ofu_channels
  const channelsSet = new Set(ofuChannels || [])

  if (
    oldChannel &&
    ECampaignChannel[oldChannel as keyof typeof ECampaignChannel] &&
    (!ofuChannels || ofuChannels.length === 0)
  ) {
    channelsSet.add(oldChannel)
  }

  return Array.from(channelsSet)
}

const CAMPAIGN_CHANNEL_LABELS: Record<ECampaignChannel, string> = {
  [ECampaignChannel.phone]: 'Phone',
  [ECampaignChannel.email]: 'Email',
  [ECampaignChannel.text]: 'Text',
}

export type TCampaignIncompatibleAssistant = {
  id: string
  label: string
  unsupportedChannels: ECampaignChannel[]
  unsupportedChannelLabels: string[]
}

export const getCampaignChannelLabel = (channel: ECampaignChannel): string =>
  CAMPAIGN_CHANNEL_LABELS[channel] || channel

export const formatHumanList = (items: string[]): string => {
  if (!items.length) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

export const getAgentUnsupportedChannels = (
  agent: TAgent | null | undefined,
  channels: ECampaignChannel[],
): ECampaignChannel[] => {
  if (!agent || !channels.length) return []

  const enabledByChannel = new Map<ECampaignChannel, boolean>(
    getChannelOptions(agent).map((option) => [
      option.value as ECampaignChannel,
      option.enabled,
    ]),
  )

  return channels.filter((channel) => !enabledByChannel.get(channel))
}

export const getIncompatibleAssistantsForChannels = ({
  assistants,
  selectedAssistantIds,
  channels,
}: {
  assistants: TAgent[]
  selectedAssistantIds: string[]
  channels: ECampaignChannel[]
}): TCampaignIncompatibleAssistant[] => {
  if (!selectedAssistantIds.length || !channels.length) return []

  const selectedIdsSet = new Set(selectedAssistantIds.map(String))

  return assistants
    .filter((assistant) => selectedIdsSet.has(String(assistant.id)))
    .map((assistant) => {
      const unsupportedChannels = getAgentUnsupportedChannels(
        assistant,
        channels,
      )
      const label =
        assistant.agentReferenceName?.trim() ||
        assistant.businessName?.trim() ||
        assistant.agentName?.trim() ||
        String(assistant.id)

      return {
        id: String(assistant.id),
        label,
        unsupportedChannels,
        unsupportedChannelLabels: unsupportedChannels.map(
          getCampaignChannelLabel,
        ),
      }
    })
    .filter((assistant) => assistant.unsupportedChannels.length > 0)
}

export type TContactWithSourceRowIndex = {
  contact: TMappedContact
  sourceRowIndex: number
}

export const transformCSVToContactsWithSourceRowIndex = (
  csvData: string[][],
  headers: string[],
  firstNameColumn: string,
  lastNameColumn: string,
  emailColumn: string,
  phoneColumn: string,
  textOptedOutColumn: string,
  emailUnsubscribedColumn: string,
  customProperties: TCustomProperty[],
): TContactWithSourceRowIndex[] => {
  function parseCsvBoolean(value?: string | null): boolean | undefined {
    const normalized = value?.trim().toLowerCase()
    const isTruthy = Boolean(
      normalized && ['true', '1', 'yes', 'y'].includes(normalized),
    )
    const isFalsy = Boolean(
      normalized && ['false', '0', 'no', 'n'].includes(normalized),
    )
    let parsedValue: boolean | undefined

    if (isTruthy) {
      parsedValue = true
    }

    if (isFalsy) {
      parsedValue = false
    }

    return parsedValue
  }

  return csvData
    .map((row, sourceRowIndex) => {
      const contact: TMappedContact = {}

      const firstNameIndex = headers.indexOf(firstNameColumn)
      const lastNameIndex = headers.indexOf(lastNameColumn)
      const emailIndex = headers.indexOf(emailColumn)
      const phoneIndex = headers.indexOf(phoneColumn)
      const textOptedOutIndex = headers.indexOf(textOptedOutColumn)
      const emailUnsubscribedIndex = headers.indexOf(emailUnsubscribedColumn)

      if (firstNameIndex !== -1)
        contact.firstName = stripExtraQuotes(row[firstNameIndex]) || undefined
      if (lastNameIndex !== -1)
        contact.lastName = stripExtraQuotes(row[lastNameIndex]) || undefined
      if (emailIndex !== -1)
        contact.email = stripExtraQuotes(row[emailIndex]) || undefined
      if (phoneIndex !== -1)
        contact.phone = stripExtraQuotes(row[phoneIndex]) || undefined
      if (textOptedOutIndex !== -1)
        contact.textOptedOut = parseCsvBoolean(
          stripExtraQuotes(row[textOptedOutIndex]),
        )
      if (emailUnsubscribedIndex !== -1)
        contact.emailUnsubscribed = parseCsvBoolean(
          stripExtraQuotes(row[emailUnsubscribedIndex]),
        )

      customProperties.forEach((prop) => {
        const columnIndex = headers.indexOf(prop.column)
        if (columnIndex !== -1) {
          const propertyName =
            prop.name === 'custom' ? prop.customName : prop.name
          if (propertyName) {
            contact[propertyName] =
              stripExtraQuotes(row[columnIndex]) || undefined
          }
        }
      })

      return {
        contact,
        sourceRowIndex,
      }
    })
    .filter(({ contact }) => contact.email || contact.phone)
}

export const transformCSVToContacts = (
  csvData: string[][],
  headers: string[],
  firstNameColumn: string,
  lastNameColumn: string,
  emailColumn: string,
  phoneColumn: string,
  textOptedOutColumn: string,
  emailUnsubscribedColumn: string,
  customProperties: TCustomProperty[],
): TMappedContact[] =>
  transformCSVToContactsWithSourceRowIndex(
    csvData,
    headers,
    firstNameColumn,
    lastNameColumn,
    emailColumn,
    phoneColumn,
    textOptedOutColumn,
    emailUnsubscribedColumn,
    customProperties,
  ).map(({ contact }) => contact)

const normalizeCsvHeader = (header: string) =>
  header.toLowerCase().replace(/[^a-z0-9]/g, '')

export const getAutoMappedContactColumns = (
  csvTableHeaders: string[],
): TAutoMappedContactColumns => {
  const findMappedHeader = (normalizedTargets: string[]) =>
    csvTableHeaders.find((header) =>
      normalizedTargets.includes(normalizeCsvHeader(header)),
    ) || ''

  return {
    firstNameColumn: findMappedHeader(['firstname', 'first', 'name']),
    lastNameColumn: findMappedHeader(['lastname', 'last']),
    emailColumn: findMappedHeader(['email']),
    phoneColumn: findMappedHeader(['phone']),
    textOptedOutColumn: findMappedHeader([
      'textoptedout',
      'optedout',
      'textoptout',
      'smsoptout',
      'smssubscribed',
      'donottext',
    ]),
    emailUnsubscribedColumn: findMappedHeader([
      'emailunsubscribed',
      'unsubscribed',
      'emailoptout',
      'optoutemail',
      'donotemail',
    ]),
  }
}

export const getIconColor = (status: EOutreachStatus) => {
  if (status === EOutreachStatus.pending) return 'warning'
  if (status === EOutreachStatus.inProgress) return 'info'
  if (status === EOutreachStatus.failed) return 'error'
  if (
    [
      EOutreachStatus.sent,
      EOutreachStatus.delivered,
      EOutreachStatus.read,
      EOutreachStatus.answered,
      EOutreachStatus.responded,
    ].includes(status)
  )
    return 'success'
  if ([EOutreachStatus.voicemail, EOutreachStatus.ringing].includes(status))
    return 'primary'
  if (
    [
      EOutreachStatus.notAnswered,
      EOutreachStatus.busy,
      EOutreachStatus.noAnswer,
      EOutreachStatus.cancelled,
    ].includes(status)
  )
    return 'error'
  return 'default'
}

export const getEmailDeliverRates = (
  emailMetrics: TCampaignEmailMetrics | null | undefined,
) => {
  if (!emailMetrics)
    return Array(3)
      .fill(1)
      .map((_, index) => ({
        label: `Delivered rate ${index + 1}`,
        value: 0,
        rate: '',
      }))

  return [
    {
      label: `${emailMetrics[EEmailStatus.Delivered]} delivered`,
      value: `${Math.round(
        (emailMetrics[EEmailStatus.Delivered] /
          (emailMetrics[EEmailStatus.Sent] || 1)) *
          100,
      )}%`,
    },
    {
      label: `${emailMetrics[EEmailStatus.Bounced]} bounced`,
      value: `${Math.round(
        (emailMetrics[EEmailStatus.Bounced] /
          (emailMetrics[EEmailStatus.Sent] || 1)) *
          100,
      )}%`,
    },
    {
      label: `${emailMetrics[EEmailStatus.Complained]} spam reported`,
      value: `${Math.round(
        (emailMetrics[EEmailStatus.Complained] /
          (emailMetrics[EEmailStatus.Sent] || 1)) *
          100,
      )}%`,
    },
  ]
}

export const getEmailReceiptEngagement = (
  emailMetrics: TCampaignEmailMetrics | null | undefined,
) => {
  if (!emailMetrics)
    return Array(3)
      .fill(1)
      .map((_, index) => ({
        label: `Receiptt engagement ${index + 1}`,
        value: 0,
        rate: '',
      }))

  const engagement = [
    {
      label: 'Sent',
      value: emailMetrics[EEmailStatus.Sent],
    },
    {
      label: `${emailMetrics[EEmailStatus.Opened]} opened`,
      value: `${Math.round(
        (emailMetrics[EEmailStatus.Opened] /
          (emailMetrics[EEmailStatus.Sent] || 1)) *
          100,
      )}%`,
    },
    {
      label: `${emailMetrics[EEmailStatus.Clicked]} clicked`,
      value: `${Math.round(
        (emailMetrics[EEmailStatus.Clicked] /
          (emailMetrics[EEmailStatus.Sent] || 1)) *
          100,
      )}%`,
    },
  ]
  return engagement
}

export const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value
  }

  return formatLocaleDateTime(date) || value
}

export const getNormalizedOfuCadence = (cadence: string = '') => {
  const legacyCadenceMap: Record<string, string> = {
    ...EOfuCadence,
    [EOfuCadence.normal]: EOfuCadence.balanced,
    [EOfuCadence.soft]: EOfuCadence.relaxed,
  }
  return legacyCadenceMap[cadence] ?? ''
}

const numberFormatter = new Intl.NumberFormat('en-US')
export const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '—'
  }

  return numberFormatter.format(value)
}

type TSelectedContactsCountParams = {
  selectedCount: number
  filteredCount: number
  useAllSelection: boolean
  deselectedCount: number
}

export const getSelectedContactsCount = ({
  selectedCount,
  filteredCount,
  useAllSelection,
  deselectedCount,
}: TSelectedContactsCountParams) => {
  if (!useAllSelection) {
    return selectedCount
  }

  if (deselectedCount === 0) {
    return filteredCount
  }

  return Math.max(filteredCount - deselectedCount, 0)
}

export const formatError = (error: string): string => {
  try {
    const parsed = JSON.parse(error)
    // helps to format the error in a more readable way
    return JSON.stringify(parsed, null, 2)
  } catch {
    return error
  }
}
